import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import PageHeader from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";
import { Users, Building2, Grid3x3, RefreshCw, History, HelpCircle, Copy, Settings2, Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import MultiSelect from "@/components/ui/multi-select";
import EntityRoleDefsDialog from "@/components/access/EntityRoleDefsDialog";
import { Label } from "@/components/ui/label";

// Iter 253m · Team Access Matrix
//
// Bulk membership editor across clients, brands, and projects.
// Toggle orientation: `members_x_entities` shows Members as rows,
// entities as columns (best for "who has access to what"); flip to
// `entities_x_members` to answer "who is on this project".
//
// Selecting a cell queues an add/remove; the pending set is applied via
// POST /workspaces/{ws}/team-matrix/bulk-assign — idempotent.

const KINDS = [
  { value: "clients", label: "Clients", singular: "client", icon: Users },
  { value: "brands", label: "Brands", singular: "brand", icon: Building2 },
  { value: "projects", label: "Projects", singular: "project", icon: Grid3x3 },
];

export default function TeamAccessMatrix() {
  const { activeWorkspaceId } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kindTab, setKindTab] = useState("projects");
  const [orientation, setOrientation] = useState("members_x_entities");
  const [pending, setPending] = useState({}); // {"kind:eid:uid": {action, role?}}
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("_all");   // R94 · department filter (legacy single-select — kept for backwards compat, superseded by selectedDepts)
  // R138d · Feb 2026 — Sortable / searchable / multi-select filters
  // for Departments, Members and Entities. Empty = show everything.
  // Department cascades into Members: selected depts narrow the
  // Member picker options; clearing depts restores full member list.
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  // Iter 253n · Role picker + audit log.
  const [rolesByKind, setRolesByKind] = useState(null);
  const [defaultRoleByKind, setDefaultRoleByKind] = useState({});
  const [selectedRole, setSelectedRole] = useState("");   // active role for new "add" clicks
  // Iter 253z-20 · Role catalog (definitions of what each client/brand role
  // actually grants). Loaded once on mount; used to render an inline
  // popover next to the Role dropdown so admins can see semantics.
  const [roleCatalog, setRoleCatalog] = useState(null);
  useEffect(() => {
    api.get(`/team-matrix/role-catalog`).then((r) => setRoleCatalog(r.data || null)).catch(() => setRoleCatalog(null));
  }, []);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Feb 2026 · Clone Access dialog + workspace-defaults dialog + dismissible info card
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState("");
  const [cloneTarget, setCloneTarget] = useState("");
  const [cloneMode, setCloneMode] = useState("add_only");
  const [cloneKinds, setCloneKinds] = useState({ client: true, brand: true, project: true });
  const [cloneBusy, setCloneBusy] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [wsDefaults, setWsDefaults] = useState({});
  const [defaultsBusy, setDefaultsBusy] = useState(false);
  // R138d · Feb 2026 — Customisable role catalog dialog.
  const [rolesDefsOpen, setRolesDefsOpen] = useState(false);
  const [infoCollapsed, setInfoCollapsed] = useState(
    () => localStorage.getItem("team-matrix-info-collapsed") === "1",
  );
  const dismissInfo = () => {
    localStorage.setItem("team-matrix-info-collapsed", "1");
    setInfoCollapsed(true);
  };

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [{ data }, rolesRes] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/team-matrix`),
        api.get(`/workspaces/${activeWorkspaceId}/team-matrix/allowed-roles`).catch(() => null),
      ]);
      setMatrix(data);
      setPending({});
      if (rolesRes?.data) {
        setRolesByKind(rolesRes.data.roles || {});
        setDefaultRoleByKind(rolesRes.data.defaults || {});
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load matrix")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);

  const loadAudit = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/team-matrix/audit-log?limit=50`);
      setAuditRows(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load audit log")); }
    finally { setAuditLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { if (auditOpen) loadAudit(); }, [auditOpen, loadAudit]);

  // Feb 2026 · Load workspace-configured default entity roles when opening dialog.
  const loadDefaults = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setDefaultsBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/team-matrix/defaults`);
      setWsDefaults(data?.defaults || {});
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load defaults")); }
    finally { setDefaultsBusy(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { if (defaultsOpen) loadDefaults(); }, [defaultsOpen, loadDefaults]);
  const saveDefaults = async () => {
    setDefaultsBusy(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/team-matrix/defaults`, wsDefaults);
      toast.success("Default roles saved");
      setDefaultsOpen(false);
      // Refresh the module-level defaults so new adds pick them up.
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save defaults")); }
    finally { setDefaultsBusy(false); }
  };
  const runClone = async () => {
    if (!cloneSource || !cloneTarget) { toast.error("Pick source and target members"); return; }
    if (cloneSource === cloneTarget) { toast.error("Source and target must be different"); return; }
    const kinds = Object.entries(cloneKinds).filter(([, v]) => v).map(([k]) => k);
    if (kinds.length === 0) { toast.error("Pick at least one kind"); return; }
    setCloneBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/team-matrix/clone-access`,
        { source_user_id: cloneSource, target_user_id: cloneTarget, mode: cloneMode, kinds },
      );
      toast.success(`Cloned ${cloneMode === "mirror" ? "(mirror)" : "(add-only)"} — added ${data.added}, removed ${data.removed}, unchanged ${data.unchanged}`);
      setCloneOpen(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Clone failed")); }
    finally { setCloneBusy(false); }
  };

  const members = matrix?.members || [];
  const entities = matrix?.entities?.[kindTab] || [];
  const memberships = matrix?.memberships?.[kindTab] || {};
  // R138h · Feb 2026 — Per-cell role map for the inline role picker.
  const memberRoles = matrix?.member_roles?.[kindTab] || {};

  const filteredEntities = useMemo(() => {
    // R94 · Sort alphabetically so lists are predictable across sub-tabs.
    const sorted = [...entities].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    // R138d · Multi-select entity picker takes precedence when it has
    // values. Otherwise fall back to the free-text comma-separated
    // filter for backwards compat.
    let out = sorted;
    if (selectedEntityIds.length > 0) {
      const wanted = new Set(selectedEntityIds);
      out = out.filter((e) => wanted.has(e.id));
    }
    // R94 · Multi-term search: comma-separated tokens → OR match.
    const terms = (q || "").toLowerCase().split(",").map((t) => t.trim()).filter(Boolean);
    if (!terms.length) return out;
    return out.filter((e) => {
      const hay = (e.name || "").toLowerCase();
      return terms.some((t) => hay.includes(t));
    });
  }, [entities, q, selectedEntityIds]);
  const filteredMembers = useMemo(() => {
    // R138d · Feb 2026 — Sort so members with real names surface
    // first; placeholder rows (name === "—") sink to the bottom.
    const isPlaceholder = (m) => !((m.name || m.email || "").trim()) || (m.name || "").trim() === "—";
    const sorted = [...members].sort((a, b) => {
      const ap = isPlaceholder(a), bp = isPlaceholder(b);
      if (ap !== bp) return ap ? 1 : -1;
      return (a.name || a.email || "").localeCompare(b.name || b.email || "", undefined, { sensitivity: "base" });
    });
    // R138d · Department multi-select cascades — empty = every dept.
    const activeDepts = selectedDepts.length > 0
      ? selectedDepts.map((d) => d.toLowerCase())
      : (deptFilter && deptFilter !== "_all" ? [deptFilter.toLowerCase()] : []);
    const byDept = activeDepts.length === 0
      ? sorted
      : sorted.filter((m) => activeDepts.includes((m.department || "").toLowerCase()));
    // R138d · Multi-select member picker takes precedence when set.
    let byPick = byDept;
    if (selectedMemberIds.length > 0) {
      const wanted = new Set(selectedMemberIds);
      byPick = byPick.filter((m) => wanted.has(m.id));
    }
    const terms = (q || "").toLowerCase().split(",").map((t) => t.trim()).filter(Boolean);
    if (!terms.length) return byPick;
    return byPick.filter((m) => {
      const hay = `${m.name || ""} ${m.email || ""}`.toLowerCase();
      return terms.some((t) => hay.includes(t));
    });
  }, [members, q, deptFilter, selectedDepts, selectedMemberIds]);
  // R94 · Departments derived from member list for the filter dropdown.
  // R138e · Feb 2026 — Fall back to fetching workspace departments
  // from `/departments` so the picker always has options even before
  // any workspace_member row carries a `department` value.
  const departments = useMemo(() => {
    const s = new Set();
    (members || []).forEach((m) => { if (m.department) s.add(m.department); });
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [members]);
  const [workspaceDepartments, setWorkspaceDepartments] = useState([]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/departments`)
      .then((r) => setWorkspaceDepartments(
        (r.data || []).map((d) => d.name).filter(Boolean)
                       .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))))
      .catch(() => setWorkspaceDepartments([]));
  }, [activeWorkspaceId]);
  const combinedDepartments = useMemo(() => {
    const s = new Set([...departments, ...workspaceDepartments]);
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [departments, workspaceDepartments]);

  const singularForKind = KINDS.find((k) => k.value === kindTab).singular;
  const availableRoles = (rolesByKind && rolesByKind[singularForKind]) || [];
  const effectiveDefaultRole = defaultRoleByKind[singularForKind] || "";
  const activeAddRole = selectedRole || effectiveDefaultRole;
  // Reset the role picker when switching kinds so a project role never
  // leaks into a client "add" action (backend would reject it, but the
  // UI should never show an invalid selection).
  useEffect(() => { setSelectedRole(""); }, [kindTab]);

  const isMember = (eid, uid) => {
    const key = `${singularForKind}:${eid}:${uid}`;
    if (pending[key]) return pending[key].action === "add";
    return (memberships[eid] || []).includes(uid);
  };
  const toggle = (eid, uid) => {
    const key = `${singularForKind}:${eid}:${uid}`;
    const currentlyMember = (memberships[eid] || []).includes(uid);
    setPending((prev) => {
      const next = { ...prev };
      if (next[key]) {
        // second click cancels
        delete next[key];
      } else if (currentlyMember) {
        next[key] = { action: "remove" };
      } else {
        next[key] = { action: "add", role: activeAddRole || undefined };
      }
      return next;
    });
  };

  const pendingCount = Object.keys(pending).length;

  const apply = async () => {
    if (!pendingCount) return;
    setSaving(true);
    try {
      const assignments = Object.entries(pending).map(([key, val]) => {
        const [kind, entity_id, user_id] = key.split(":");
        const row = { user_id, kind, entity_id, action: val.action };
        // R138h · pass role for add + update_role.
        if ((val.action === "add" || val.action === "update_role") && val.role) row.role = val.role;
        return row;
      });
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/team-matrix/bulk-assign`,
        { assignments });
      toast.success(`Applied: +${data.added} added, −${data.removed} removed, ${data.unchanged} unchanged.`);
      if ((data.errors || []).length) {
        toast.warning(`${data.errors.length} row(s) rejected (see console).`);
        // eslint-disable-next-line no-console
        console.warn("team-matrix bulk-assign errors", data.errors);
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk apply failed")); }
    finally { setSaving(false); }
  };

  const bulkForRow = (m, action) => {
    setPending((prev) => {
      const next = { ...prev };
      filteredEntities.forEach((e) => {
        const isNow = (memberships[e.id] || []).includes(m.id);
        const shouldBe = action === "add";
        if (isNow === shouldBe) return;
        next[`${singularForKind}:${e.id}:${m.id}`] = action === "add"
          ? { action: "add", role: activeAddRole || undefined }
          : { action: "remove" };
      });
      return next;
    });
  };
  const bulkForCol = (e, action) => {
    setPending((prev) => {
      const next = { ...prev };
      filteredMembers.forEach((m) => {
        const isNow = (memberships[e.id] || []).includes(m.id);
        const shouldBe = action === "add";
        if (isNow === shouldBe) return;
        next[`${singularForKind}:${e.id}:${m.id}`] = action === "add"
          ? { action: "add", role: activeAddRole || undefined }
          : { action: "remove" };
      });
      return next;
    });
  };

  const isMembersRows = orientation === "members_x_entities";

  return (
    <div data-testid="team-matrix-page">
      <PageHeader
        eyebrow="Access"
        title="Team Access Matrix"
        description="Bulk add or remove members across clients, brands, and projects. Toggle orientation to answer either 'who has access to what' or 'who is on this project'."
        testid="team-matrix-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none h-9" onClick={() => setCloneOpen(true)} data-testid="team-matrix-clone-btn">
              <Copy className="w-4 h-4 mr-1" /> Clone access
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => setDefaultsOpen(true)} data-testid="team-matrix-defaults-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Defaults
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => setRolesDefsOpen(true)} data-testid="team-matrix-role-defs-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Access roles
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={() => setAuditOpen(true)} data-testid="team-matrix-history-btn">
              <History className="w-4 h-4 mr-1" /> History
            </Button>
            <Button variant="outline" className="rounded-none h-9" onClick={load} disabled={loading} data-testid="team-matrix-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button className="rounded-none h-9" onClick={apply} disabled={!pendingCount || saving} data-testid="team-matrix-apply">
              Apply {pendingCount > 0 ? `(${pendingCount})` : ""}
            </Button>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 space-y-3">
        {!infoCollapsed && (
          <div className="border border-primary/40 bg-primary/5 p-3 relative" data-testid="team-matrix-info-card">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
              <div className="text-[12px] space-y-1.5 flex-1 pr-6">
                <div className="font-semibold">How access on this screen works</div>
                <div>
                  Every user gets access to a Client / Brand / Project through <b>two independent layers</b>:
                </div>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <b>Entity role</b> (this screen — <i>owner / manager / member / viewer</i> for
                    clients &amp; brands, plus <i>admin / designer / external</i> for projects) — controls
                    whether a user can <em>open</em> that record at all, plus ownership signals like
                    notifications, &ldquo;created by&rdquo;, and external-vs-internal visibility.
                  </li>
                  <li>
                    <b>Granular sub-tab permissions</b> (Settings → Roles — <code>brand.tab.strategy.view</code>,
                    <code> project.tab.files.edit</code>, etc.) — refine <em>what tabs and fields</em> a
                    member sees <em>within</em> a record they can already open.
                  </li>
                </ul>
                <div className="text-muted-foreground pt-1">
                  These two layers <b>don&apos;t conflict</b> — they layer. Entity role = &ldquo;can this user
                  open the record?&rdquo;; granular perm = &ldquo;which tabs / fields inside?&rdquo;. Use the <b>Defaults</b>
                  button to set a workspace-wide default entity role per kind, so bulk-adds don&apos;t need per-cell picking.
                </div>
              </div>
              <button
                type="button"
                onClick={dismissInfo}
                className="absolute top-2 right-2 text-muted-foreground hover:text-foreground text-xs px-1"
                aria-label="Dismiss"
                data-testid="team-matrix-info-dismiss"
              >×</button>
            </div>
          </div>
        )}
        {infoCollapsed && (
          <button
            type="button"
            className="text-[10px] text-muted-foreground underline"
            onClick={() => setInfoCollapsed(false)}
            data-testid="team-matrix-info-show"
          >
            Show explanation of entity roles vs granular permissions
          </button>
        )}
        <div className="border border-border bg-muted/40 p-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-1 border border-border p-0.5 bg-background" data-testid="team-matrix-kind-tabs">
            {KINDS.map((k) => (
              <button key={k.value} type="button" onClick={() => setKindTab(k.value)}
                       className={`px-3 py-1 text-[11px] font-medium ${kindTab === k.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                       data-testid={`team-matrix-kind-${k.value}`}>
                {k.label}
              </button>
            ))}
          </div>
          <Select value={orientation} onValueChange={setOrientation}>
            <SelectTrigger className="h-8 w-64 text-xs rounded-none" data-testid="team-matrix-orientation">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="members_x_entities">Rows = Members / Cols = Entities</SelectItem>
              <SelectItem value="entities_x_members">Rows = Entities / Cols = Members</SelectItem>
            </SelectContent>
          </Select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Free text filter (comma-separate for OR)…"
                 className="h-8 text-xs w-52 rounded-none" data-testid="team-matrix-filter" />
          {/* R138d/e · Feb 2026 — Sortable / searchable / multi-select
              filters. Department picker sourced from the workspace's
              `departments` collection (with legacy member-string
              fallback) so it renders even before any user has been
              linked to a dept. */}
          <MultiSelect
            options={combinedDepartments.map((d) => ({ value: d, label: d, testid: `team-matrix-dept-opt-${d}` }))}
            value={selectedDepts}
            onChange={setSelectedDepts}
            placeholder="Department"
            allLabel={combinedDepartments.length === 0 ? "No departments configured" : "All departments"}
            width="w-44"
            testid="team-matrix-dept-filter"
            maxChips={1}
          />
          <MultiSelect
            options={(selectedDepts.length > 0
                ? members.filter((m) => selectedDepts.map((d) => d.toLowerCase()).includes((m.department || "").toLowerCase()))
                : members
              ).map((m) => ({ value: m.id, label: m.name || m.email || m.id, testid: `team-matrix-member-opt-${m.id}` }))}
            value={selectedMemberIds}
            onChange={setSelectedMemberIds}
            placeholder="Members"
            allLabel="All members"
            width="w-52"
            testid="team-matrix-member-filter"
            maxChips={1}
          />
          <MultiSelect
            options={entities.map((e) => ({ value: e.id, label: e.name || e.id, testid: `team-matrix-entity-opt-${e.id}` }))}
            value={selectedEntityIds}
            onChange={setSelectedEntityIds}
            placeholder={KINDS.find((k) => k.value === kindTab)?.label || "Entities"}
            allLabel={`All ${KINDS.find((k) => k.value === kindTab)?.label?.toLowerCase() || "entities"}`}
            width="w-52"
            testid="team-matrix-entity-filter"
            maxChips={1}
          />
          {(selectedDepts.length > 0 || selectedMemberIds.length > 0 || selectedEntityIds.length > 0 || (q || "").trim()) && (
            <button type="button"
                    className="text-[10px] text-muted-foreground underline"
                    onClick={() => { setSelectedDepts([]); setSelectedMemberIds([]); setSelectedEntityIds([]); setQ(""); setDeptFilter("_all"); }}
                    data-testid="team-matrix-clear-filters">
              Clear filters
            </button>
          )}
          {availableRoles.length > 0 && (
            <div className="inline-flex items-center gap-1" data-testid="team-matrix-role-picker">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Access role:</span>
              <Select value={activeAddRole || ""} onValueChange={(v) => setSelectedRole(v)}>
                <SelectTrigger className="h-8 w-32 text-xs rounded-none" data-testid="team-matrix-role-select">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`team-matrix-role-opt-${r}`}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Iter 253z-20 · role-definition popover so admins can see
                    exactly what each role grants. Client/brand roles have
                    concrete permission sets returned from the backend;
                    project roles use the workspace permission catalog. */}
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button"
                          className="text-muted-foreground hover:text-foreground p-1"
                          aria-label="What does this role mean?"
                          data-testid="team-matrix-role-help">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[420px] text-xs p-3 max-h-[70vh] overflow-y-auto" side="bottom" align="end">
                  <div className="font-semibold text-xs mb-2 uppercase tracking-widest text-muted-foreground">
                    {singularForKind} access-role definitions
                  </div>
                  {/* R138d · Feb 2026 — Make it crystal clear these
                      compose ON TOP OF workspace permissions, and are
                      scoped to this one client/brand/project. Users
                      historically read this as an either/or picker. */}
                  <div className="mb-3 p-2 border border-primary/40 bg-primary/5 text-[11px]">
                    <div className="font-semibold text-[10px] uppercase tracking-widest text-primary mb-1">
                      How this composes
                    </div>
                    <ul className="list-disc pl-4 space-y-0.5 text-foreground/80">
                      <li>Stacks <b>on top of</b> the user&apos;s workspace permissions — it does not replace them.</li>
                      <li>Scoped to this specific {singularForKind} only — same person can hold different roles across {singularForKind}s.</li>
                      <li>Workspace admins (<code>workspace.manage</code>) always effectively get <b>owner</b> everywhere.</li>
                    </ul>
                  </div>
                  {(singularForKind === "client" || singularForKind === "brand")
                    && roleCatalog?.client_brand ? (
                    <div className="space-y-2">
                      {availableRoles.map((r) => {
                        const info = roleCatalog.client_brand[r];
                        if (!info) return null;
                        const isActive = activeAddRole === r;
                        return (
                          <div key={r}
                               className={`p-2 border ${isActive ? "border-primary bg-primary/5" : "border-border"}`}
                               data-testid={`team-matrix-role-def-${r}`}>
                            <div className="font-semibold text-xs">{info.label}
                              {isActive && <span className="ml-1 text-[10px] text-primary">● selected</span>}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{info.description}</div>
                            <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-2 mb-1">Grants access to</div>
                            <div className="mt-0.5 flex flex-wrap gap-1">
                              {info.permissions.map((p) => (
                                <span key={p} className="text-[10px] px-1.5 py-0.5 border border-border bg-muted/40 rounded-none">
                                  {p}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[11px] text-muted-foreground">
                        Project roles inherit permissions from the workspace role catalog
                        (owner/admin/manager/designer/viewer/external). Manage per-permission
                        overrides in <b>Settings → Roles</b>.
                      </p>
                      <ul className="text-[11px] space-y-1">
                        {availableRoles.map((r) => (
                          <li key={r}><b>{r.charAt(0).toUpperCase() + r.slice(1)}</b></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto">
            {filteredMembers.length} member{filteredMembers.length === 1 ? "" : "s"} · {filteredEntities.length} {kindTab}
          </span>
        </div>
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && matrix && (
          <div className="overflow-auto border border-border bg-card max-h-[70vh]" data-testid="team-matrix-scroll">
            <table className="text-[11px] w-max min-w-full" data-testid="team-matrix-table">
              <thead>
                <tr className="bg-muted sticky top-0 z-20">
                  <th className="p-2 text-left border-r border-border w-64 sticky left-0 bg-muted z-30 shadow-[2px_0_0_0_hsl(var(--border))]">
                    {isMembersRows ? "Member" : "Entity"}
                  </th>
                  {(isMembersRows ? filteredEntities : filteredMembers).map((h) => (
                    <th key={h.id} className="p-1.5 text-left border-r border-border min-w-[210px]"
                        title={isMembersRows ? (h.name || h.email) : (h.email ? `${h.name || ""}\n${h.email}` : (h.name || h.email))}>
                      {/* R138d · Feb 2026 — Compact column header:
                          name + inline +all/-all pills. Email tooltip
                          shows on hover of the label. */}
                      <div className="flex items-center gap-1">
                        <div className="truncate flex-1 min-w-0 font-semibold cursor-help" data-testid={`team-matrix-col-${h.id}`}>
                          {h.name || h.email || "—"}
                        </div>
                        <button className="text-[9px] px-1 border border-border hover:bg-primary hover:text-primary-foreground flex-shrink-0"
                                type="button"
                                onClick={() => isMembersRows ? bulkForCol(h, "add") : bulkForRow(h, "add")}
                                data-testid={`team-matrix-col-${h.id}-add-all`}>+ all</button>
                        <button className="text-[9px] px-1 border border-border hover:bg-red-600 hover:text-white flex-shrink-0"
                                type="button"
                                onClick={() => isMembersRows ? bulkForCol(h, "remove") : bulkForRow(h, "remove")}
                                data-testid={`team-matrix-col-${h.id}-remove-all`}>− all</button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(isMembersRows ? filteredMembers : filteredEntities).map((rh) => (
                  <tr key={rh.id} className="border-t border-border">
                    <td className="p-1.5 border-r border-border sticky left-0 bg-background z-10 shadow-[2px_0_0_0_hsl(var(--border))]" data-testid={`team-matrix-row-${rh.id}`}>
                      {/* R138d · Feb 2026 — Compact row: name + inline
                          bulk buttons on the same row; email surfaces
                          on hover of the name to save vertical space. */}
                      <div className="flex items-center gap-1.5">
                        <div className="font-semibold truncate flex-1 min-w-0 cursor-help"
                             title={isMembersRows && rh.email ? `${rh.name || ""}\n${rh.email}` : (rh.name || rh.email || "—")}>
                          {rh.name || rh.email || "—"}
                        </div>
                        <button className="text-[9px] px-1 border border-border hover:bg-primary hover:text-primary-foreground flex-shrink-0"
                                type="button"
                                onClick={() => isMembersRows ? bulkForRow(rh, "add") : bulkForCol(rh, "add")}
                                data-testid={`team-matrix-row-${rh.id}-add-all`}>+ all</button>
                        <button className="text-[9px] px-1 border border-border hover:bg-red-600 hover:text-white flex-shrink-0"
                                type="button"
                                onClick={() => isMembersRows ? bulkForRow(rh, "remove") : bulkForCol(rh, "remove")}
                                data-testid={`team-matrix-row-${rh.id}-remove-all`}>− all</button>
                      </div>
                    </td>
                    {(isMembersRows ? filteredEntities : filteredMembers).map((ch) => {
                      const eid = isMembersRows ? ch.id : rh.id;
                      const uid = isMembersRows ? rh.id : ch.id;
                      const on = isMember(eid, uid);
                      const currentRole = memberRoles?.[eid]?.[uid];
                      const key = `${singularForKind}:${eid}:${uid}`;
                      const p = pending[key];
                      const pendingRole = p?.action === "add" ? (p.role || activeAddRole || effectiveDefaultRole) : null;
                      const pendingRoleChange = p?.action === "update_role" ? p.role : null;
                      const displayRole = pendingRole || pendingRoleChange || currentRole;
                      return (
                        <td key={ch.id} className="border-r border-border align-middle p-1">
                          <div className="flex items-center gap-1 justify-start">
                            <button
                              type="button"
                              onClick={() => toggle(eid, uid)}
                              className={`w-6 h-6 border flex-shrink-0 ${on ? "bg-emerald-500 border-emerald-600 text-white" : "bg-background border-border"} ${p ? "ring-2 ring-yellow-400" : ""}`}
                              data-testid={`team-matrix-cell-${eid}-${uid}`}
                              aria-label={on ? "Remove" : "Add"}
                              title={pendingRole ? `Grant as ${pendingRole}` : (p?.action === "remove" ? "Remove" : (on ? `Active · ${currentRole}` : "Not a member"))}
                            >
                              {on ? "✓" : ""}
                            </button>
                            {/* R138h · Feb 2026 — Show the current
                                access role next to the checkbox when
                                the cell is active. Clicking opens a
                                dropdown to change it inline. Users
                                still see the pending "add as <role>"
                                pill for cells they just ticked. */}
                            {(on || pendingRole) && displayRole && (
                              <div className={`inline-flex items-center ${(pendingRole || pendingRoleChange) ? "ring-2 ring-yellow-400 bg-yellow-50" : ""}`}
                                    data-testid={`team-matrix-cell-role-wrap-${eid}-${uid}`}>
                                <Select
                                  value={displayRole}
                                  onValueChange={(newRole) => {
                                    if (newRole === currentRole && !pendingRole) return;
                                    if (pendingRole) {
                                      setPending((prev) => ({ ...prev, [key]: { ...prev[key], role: newRole } }));
                                    } else {
                                      setPending((prev) => ({
                                        ...prev,
                                        [key]: {
                                          kind: singularForKind,
                                          entity_id: eid,
                                          user_id: uid,
                                          action: "update_role",
                                          role: newRole,
                                        },
                                      }));
                                    }
                                  }}
                                >
                                  <SelectTrigger
                                    className={`h-6 w-auto min-w-[80px] max-w-[110px] text-[10px] rounded-none px-1 ${(pendingRole || pendingRoleChange) ? "!border-yellow-500 !bg-yellow-50 !text-yellow-800 font-semibold" : ""}`}
                                    data-testid={`team-matrix-cell-role-${eid}-${uid}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableRoles.map((r) => (
                                      <SelectItem key={r} value={r} data-testid={`team-matrix-cell-role-${eid}-${uid}-opt-${r}`}>
                                        {r.charAt(0).toUpperCase() + r.slice(1)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {pendingCount > 0 && (
              <div className="p-2 border-t border-border bg-yellow-50 text-yellow-800 text-[11px] flex items-center gap-2" data-testid="team-matrix-pending-badge">
                <Badge variant="outline" className="border-yellow-500 text-yellow-800">{pendingCount} change(s) pending</Badge>
                {activeAddRole && (
                  <Badge variant="outline" className="border-yellow-500 text-yellow-800" data-testid="team-matrix-pending-role">
                    New adds → <strong>{activeAddRole}</strong>
                  </Badge>
                )}
                <span>Click Apply to save.</span>
              </div>
            )}
          </div>
        )}
      </div>
      <Sheet open={auditOpen} onOpenChange={setAuditOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="team-matrix-audit-drawer">
          <SheetHeader>
            <SheetTitle>Access audit log</SheetTitle>
            <SheetDescription>
              Every bulk-assign batch is recorded here. Read-only — use it to
              trace who added or removed access to which entity.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {auditLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!auditLoading && auditRows.length === 0 && (
              <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center" data-testid="team-matrix-audit-empty">
                No bulk-assign batches recorded yet.
              </div>
            )}
            {!auditLoading && auditRows.map((row) => (
              <div key={row.id} className="border border-border p-3 space-y-2" data-testid={`team-matrix-audit-row-${row.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs">
                    <span className="font-semibold">{row.actor_name || "Admin"}</span>
                    <span className="text-muted-foreground"> · {new Date(row.at).toLocaleString()}</span>
                  </div>
                  <div className="flex gap-1">
                    {row.added > 0 && <Badge variant="outline" className="text-[9px] border-emerald-500 text-emerald-700">+{row.added} added</Badge>}
                    {row.removed > 0 && <Badge variant="outline" className="text-[9px] border-red-500 text-red-700">−{row.removed} removed</Badge>}
                  </div>
                </div>
                <div className="space-y-1 pl-1 border-l border-border">
                  {(row.events || []).slice(0, 20).map((ev, i) => (
                    <div key={i} className="text-[11px] flex items-center gap-2 flex-wrap" data-testid={`team-matrix-audit-event-${row.id}-${i}`}>
                      <Badge variant="outline" className={`text-[9px] rounded-none ${ev.action === "add" ? "border-emerald-500 text-emerald-700" : "border-red-500 text-red-700"}`}>
                        {ev.action === "add" ? "+" : "−"} {ev.kind}
                      </Badge>
                      <span className="font-medium">{ev.user_name || ev.user_id}</span>
                      <span className="text-muted-foreground">{ev.action === "add" ? "→" : "×"}</span>
                      <span>{ev.entity_name || ev.entity_id}</span>
                      {ev.role && (
                        <Badge variant="secondary" className="text-[9px] rounded-none">{ev.role}</Badge>
                      )}
                    </div>
                  ))}
                  {(row.events || []).length > 20 && (
                    <div className="text-[10px] text-muted-foreground">…and {row.events.length - 20} more.</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* Feb 2026 · Clone Access dialog */}
      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid="team-matrix-clone-dialog">
          <DialogHeader>
            <DialogTitle>Clone member access</DialogTitle>
            <DialogDescription>
              Copy record-level memberships from one member to another.
              <span className="block text-[11px] text-muted-foreground mt-1">
                Roles are preserved: whatever role the source has on a record, the target picks up.
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs mb-1 block">Source member (copy FROM)</Label>
              <Select value={cloneSource} onValueChange={setCloneSource}>
                <SelectTrigger className="h-9 rounded-none" data-testid="team-matrix-clone-source">
                  <SelectValue placeholder="Pick a source…" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Target member (copy TO)</Label>
              <Select value={cloneTarget} onValueChange={setCloneTarget}>
                <SelectTrigger className="h-9 rounded-none" data-testid="team-matrix-clone-target">
                  <SelectValue placeholder="Pick a target…" />
                </SelectTrigger>
                <SelectContent>
                  {members.filter((m) => m.id !== cloneSource).map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name || m.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Mode</Label>
              <RadioGroup value={cloneMode} onValueChange={setCloneMode} className="space-y-2">
                <div className="flex items-start gap-2 border border-border p-2">
                  <RadioGroupItem value="add_only" id="clone-add-only" data-testid="team-matrix-clone-mode-add-only" />
                  <label htmlFor="clone-add-only" className="text-xs cursor-pointer">
                    <div className="font-semibold">Add-only (safer, additive)</div>
                    <div className="text-muted-foreground">Target keeps every existing membership and picks up anything source has that target didn&apos;t. Nothing is removed.</div>
                  </label>
                </div>
                <div className="flex items-start gap-2 border border-border p-2">
                  <RadioGroupItem value="mirror" id="clone-mirror" data-testid="team-matrix-clone-mode-mirror" />
                  <label htmlFor="clone-mirror" className="text-xs cursor-pointer">
                    <div className="font-semibold">Full mirror (destructive)</div>
                    <div className="text-muted-foreground">Target ends up with the EXACT same active memberships as source. Memberships target had but source doesn&apos;t will be removed.</div>
                  </label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Kinds to include</Label>
              <div className="flex gap-3 text-xs">
                {["client", "brand", "project"].map((k) => (
                  <label key={k} className="inline-flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cloneKinds[k]}
                      onChange={(e) => setCloneKinds((s) => ({ ...s, [k]: e.target.checked }))}
                      data-testid={`team-matrix-clone-kind-${k}`}
                    />
                    {k}s
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setCloneOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={runClone} disabled={cloneBusy || !cloneSource || !cloneTarget} data-testid="team-matrix-clone-run">
              {cloneBusy ? "Cloning…" : "Clone access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · Workspace default entity roles dialog */}
      <Dialog open={defaultsOpen} onOpenChange={setDefaultsOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="team-matrix-defaults-dialog">
          <DialogHeader>
            <DialogTitle>Default entity roles</DialogTitle>
            <DialogDescription>
              These roles are applied when a bulk-add cell doesn&apos;t specify an explicit role
              (e.g., &ldquo;+ all&rdquo; buttons or clones without a per-cell picker).
            </DialogDescription>
          </DialogHeader>
          {defaultsBusy && !rolesByKind ? (
            <div className="text-sm text-muted-foreground p-4">Loading…</div>
          ) : (
            <div className="space-y-3">
              {["client", "brand", "project"].map((k) => (
                <div key={k}>
                  <Label className="text-xs mb-1 block capitalize">Default {k} role</Label>
                  <Select
                    value={wsDefaults[k] || defaultRoleByKind[k] || ""}
                    onValueChange={(v) => setWsDefaults((s) => ({ ...s, [k]: v }))}
                  >
                    <SelectTrigger className="h-9 rounded-none" data-testid={`team-matrix-defaults-${k}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(rolesByKind?.[k] || []).map((r) => (
                        <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDefaultsOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={saveDefaults} disabled={defaultsBusy} data-testid="team-matrix-defaults-save">
              Save defaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* R138d · Feb 2026 — Customisable Access Role catalog. */}
      <EntityRoleDefsDialog
        open={rolesDefsOpen}
        onOpenChange={setRolesDefsOpen}
        workspaceId={activeWorkspaceId}
        onSaved={() => { load(); }}
      />
    </div>
  );
}
